import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Clipboard, calender } from '../../../assets/components/svgs';
import Card from '../../shared/cards/Card';
import InputField from '../../shared/Fields/InputField';
import { AppConstants } from '../../../constants/AppConstants';
import PickList, { PICKLIST_TYPES } from '../../shared/Fields/PickList';
import TextAreaField from '../../shared/Fields/TextAreaField';
import DependentSelector from '../DependentSelector';
import RadioButton from '../../shared/Fields/RadioButton';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isBoolean, isEmpty } from 'lodash';
import { CalendarIcon, ClockIcon } from '@heroicons/react/outline';
import { DateInputField } from '../Reusable/DateInputField';
import JSONEditor from '../../AppSettings/JSONEditor';

interface FormProps {
  isEdit: boolean;
  formData: any;
  setFormData: Function;
  formError: any;
  setFormError: Function;
}

const ActivityTab = ({ isEdit, formData, setFormData, formError, setFormError }: FormProps) => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};

  const setStartandEnd = () => {
    const { periodEnd, periodStart } = formData;

    const handleDateChange = (date: any, key: string) => {
      const updatedDate = new Date(date);
      let updatedFormData = { ...formData, [key]: updatedDate.toISOString() };
      let updatedFormError: any = {};
      if (key === 'periodStart' && periodEnd && updatedDate > new Date(periodEnd)) {
        updatedFormError = { ...updatedFormError, periodEnd: t('T_PERIOD_END_VALIDATION') };
      }

      if (key === 'periodEnd' && periodStart && updatedDate < new Date(periodStart)) {
        updatedFormError = { ...updatedFormError, periodStart: t('T_PERIOD_START_VALIDATION') };
      }

      if (!isEmpty(updatedFormError)) {
        setFormError({ ...formError, ...updatedFormError });
      } else {
        setFormError({ ...formError, periodStart: '', periodEnd: '' });
      }

      setFormData(updatedFormData);
    };

    useEffect(() => {
      try {
        if (!formData?.timing?.length && formError?.timing?.length) {
          setFormError({ ...formError, timing: '' });
          return;
        }

        JSON.parse(formData?.timing);
        setFormError({ ...formError, timing: '' });
      } catch (e: any) {
        if (formData?.timing?.length) {
          setFormError({ ...formError, timing: e?.message || t('T_ERROR_JSON') });
        }
      }
    }, [formData?.timing]);

    useEffect(() => {
      try {
        if (!formData?.code?.length && formError?.code?.length) {
          setFormError({ ...formError, code: '' });
          return;
        }

        JSON.parse(formData?.code);
        setFormError({ ...formError, code: '' });
      } catch (e: any) {
        if (formData?.code?.length) {
          setFormError({ ...formError, code: e?.message || t('T_ERROR_JSON') });
        }
      }
    }, [formData?.code]);
    return (
      <div>
        <div className="flex w-full flex-row justify-between">
          <div className="flex w-1/2 flex-row justify-between">
            <DatePicker
              selected={periodStart ? new Date(periodStart) : null}
              onChange={(date: any) => handleDateChange(date, 'periodStart')}
              fixedHeight
              // minDate={new Date()}
              showMonthDropdown
              showYearDropdown
              showTimeSelect
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              shouldCloseOnSelect={false}
              calendarClassName={'text-base'}
              customInput={
                <DateInputField
                  overrideValue={
                    periodStart ? moment(periodStart).format(AppConstants.DEFAULT_DATE_FORMAT) : ''
                  }
                  fieldKey="periodStart"
                  formError={formError}
                  label={t('T_CARE_PLAN_START_DATE')}
                  icon={CalendarIcon}
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
          </div>
          <div className="ml-4 flex w-1/2 flex-row justify-between">
            <DatePicker
              selected={periodEnd ? new Date(periodEnd) : null}
              onChange={(date: any) => handleDateChange(date, 'periodEnd')}
              fixedHeight
              showMonthDropdown
              showYearDropdown
              showTimeSelect
              scrollableYearDropdown
              minDate={periodStart ? new Date(periodStart) : undefined}
              yearDropdownItemNumber={100}
              shouldCloseOnSelect={false}
              calendarClassName={'text-base'}
              customInput={
                <DateInputField
                  overrideValue={
                    periodEnd ? moment(periodEnd).format(AppConstants.DEFAULT_DATE_FORMAT) : ''
                  }
                  fieldKey="periodEnd"
                  formError={formError}
                  label={t('T_CARE_PLAN_END_DATE')}
                  icon={CalendarIcon}
                  formData={formData}
                  setFormData={setFormData}
                />
              }
            />
          </div>
        </div>

        {/* <div className="mt-8 text-right">
          <BiButton
            className={'mr-5 border border-solid border-primary text-primary'}
            type="button"
            onClick={() => {
              dispatch(setFormData({ periodStart: '', periodEnd: '' }));
              setSelectedOption('');
            }}
          >
            {t('T_CANCEL')}
          </BiButton>

          <BiButton
            className={'border border-solid border-white bg-primary text-white'}
            type="button"
            disabled={false}
            onClick={() => {
              setSelectedOption('');
              // editTemplate();
            }}
          >
            {t('T_ACTIVATE')}
          </BiButton>
        </div> */}
      </div>
    );
  };

  return (
    // <div className="h-[90vh] overflow-scroll p-4">
    <div className="p-2">
      <Card
        title={t('T_CARE_PLAN_ACTIVITY')}
        icon={Clipboard}
        alt={t('T_CARE_PLAN_ACTIVITY')}
        height=""
        width=""
      >
        <DependentSelector
          formData={formData}
          setFormData={(value: any) => {
            setFormData({ ...formData, ...value });
          }}
          disabled={!isEdit}
          setFormError={setFormError}
          primaryFieldKey="kind"
          primaryFieldLabel={t('T_CARE_PLAN_ACTIVITY_KIND_ACTIVITY')}
          secondaryFieldKey="activityTypeId"
          secondaryFieldLabel={t('T_CARE_PLAN_ACTIVITY')}
          formError={formError}
          primaryFieldOptions={
            careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_KIND_TYPES]
          }
        ></DependentSelector>
        {/* <div className="mb-5 flex w-full flex-row flex-wrap">
          <div className="flex w-1/2 flex-col">
            <PickList
              fieldKey={'key'}
              fieldValues={['value']}
              label={t('T_CARE_PLAN_ACTIVITY_LOCATION_PERFORMANCE')}
              selectedItems={[]}
              showAddOption={false}
              onSelectedChange={(items: any) => {}}
              items={[]}
              disabled={!isEdit}
              readOnly={false}
              type={PICKLIST_TYPES.SINGLE}
            />
          </div>
        </div> */}
        <div className="mb-5 flex w-full flex-col flex-wrap">
          <TextAreaField
            fieldKey={'description'}
            type={'text'}
            id={'description'}
            label={t('T_DESCRIPTION')}
            placeholder={''}
            required={false}
            formData={formData}
            setFormData={setFormData}
            formError={formError}
            style={{ resize: 'vertical' }}
            asterixFirst
            rows={4}
            disabled={!isEdit}
          ></TextAreaField>
        </div>
        <DependentSelector
          formData={formData}
          setFormData={(value: any) => {
            setFormData({ ...formData, ...value });
          }}
          disabled={!isEdit}
          primaryFieldKey="performerType"
          primaryFieldLabel={t('T_CARE_PLAN_ACTIVITY_PERFORMER_TYPE')}
          secondaryFieldKey="performer"
          secondaryFieldLabel={t('T_CARE_PLAN_ACTIVITY_PERFORMER')}
          formError={formError}
          setFormError={setFormError}
          primaryFieldOptions={
            careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_ACTIVITIES_PERFORMER_TYPES]
          }
        ></DependentSelector>
        <div className="flex flex-col">
          <div className="flex">{t('T_CARE_PLAN_ACTIVITY_PERFORMED_CHECK')}</div>
          <div className="mt-2 flex flex-row">
            <RadioButton
              selectedField={
                isBoolean(formData?.doNotPerform) && !formData?.doNotPerform ? t('T_YES') : 'none'
              }
              option={t('T_YES')}
              name={t('T_YES')}
              label={t('T_YES')}
              onClick={() => {
                setFormData({
                  ...formData,
                  doNotPerform:
                    isBoolean(formData?.doNotPerform) && !formData?.doNotPerform ? null : false
                });
              }}
            />
            <RadioButton
              selectedField={
                isBoolean(formData?.doNotPerform) && formData?.doNotPerform ? t('T_NO') : 'none'
              }
              option={t('T_NO')}
              name={t('T_NO')}
              label={t('T_NO')}
              className="ml-4"
              onClick={() => {
                setFormData({
                  ...formData,
                  doNotPerform:
                    isBoolean(formData?.doNotPerform) && formData?.doNotPerform ? null : true
                });
              }}
            />
          </div>
        </div>
        <div className="mt-6 flex w-full flex-col">
          <JSONEditor
            setIsLoading={() => {}}
            isComponent={true}
            jsonString={formData['code']}
            setJsonString={(json: any) => {
              setFormData({ ...formData, code: json });
            }}
            disabled={!isEdit}
            showLineNumber={true}
            showFormatJSONButton={true}
            showImportJSONButton={false}
            formatJSONButtonLabel="{}"
            height="h-[20vh]"
            JSONEditorLabel={t('T_CARE_PLAN_ACTIVITY_CODE')}
          />
        </div>
      </Card>

      <Card
        title={t('T_CARE_PLAN_ACTIVITY_SCHEDULE')}
        icon={calender}
        alt={t('T_CARE_PLAN_ACTIVITY_SCHEDULE')}
        height=""
        width=""
      >
        <div className="flex w-full flex-col">
          {setStartandEnd()}{' '}
          <div className="mt-6 flex w-full flex-col">
            <JSONEditor
              setIsLoading={() => {}}
              isComponent={true}
              jsonString={formData['timing']}
              setJsonString={(json: any) => {
                setFormData({ ...formData, timing: json });
              }}
              disabled={!isEdit}
              showLineNumber={true}
              showFormatJSONButton={true}
              showImportJSONButton={false}
              formatJSONButtonLabel="{}"
              JSONEditorLabel={t('T_CARE_PLAN_ACTIVITY_TIMING')}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ActivityTab;
